// File: src/components/OmniCine/Tracks/Shared/ThumbnailStrip.tsx
import * as React from "react";
import { useThumbnails } from "@/lib/thumbs/useThumbnails";

type Props = {
  /** Media URL for this video track */
  src: string;
  /** Total seconds represented by the visible lane */
  durationSec: number;
  /** Height in CSS px of the thumbnail strip (fits inside your track row) */
  height: number;
  /** Provider: "auto" | "video" | "ffmpeg" */
  provider?: "auto" | "video" | "ffmpeg";
};

export default function ThumbnailStrip({
  src,
  durationSec,
  height,
  provider = "auto",
}: Props) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  // Request 12 thumbnails (evenly spaced) by default; hook handles caching.
  const { urls, loading } = useThumbnails(src, durationSec, {
    provider,
    height,
    frameCount: 12,
  });

  // Keep DPR stable across callbacks
  const getDpr = React.useCallback(() => {
    if (typeof window === "undefined") return 1;
    const dpr = window.devicePixelRatio || 1;
    return dpr > 0 ? dpr : 1;
  }, []);

  // Resize canvas backing store to match container (CSS px) * DPR.
  const resizeCanvas = React.useCallback(
    (canvas: HTMLCanvasElement, cssH: number) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = getDpr();

      const cssW = Math.max(64, rect.width || 0); // CSS px
      const cssHeight = Math.max(16, cssH);

      const nextW = Math.max(64, Math.floor(cssW * dpr));
      const nextH = Math.max(16, Math.floor(cssHeight * dpr));

      if (canvas.width !== nextW) canvas.width = nextW;
      if (canvas.height !== nextH) canvas.height = nextH;

      // Expose CSS height; width is controlled by layout
      canvas.style.height = `${cssHeight}px`;
    },
    [getDpr],
  );

  // Paint helper (background + optional placeholder grid)
  const paintBase = React.useCallback((ctx: CanvasRenderingContext2D) => {
    const { canvas } = ctx;
    const w = canvas.width,
      h = canvas.height;

    // Frosted-ish dark gradient to blend in with timeline lanes
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, "rgba(2, 6, 23, 0.85)");
    g.addColorStop(1, "rgba(2, 6, 23, 0.55)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Subtle checker as a placeholder until tiles arrive
    ctx.save();
    ctx.globalAlpha = 0.12;
    const size = Math.max(6, Math.floor(h / 8));
    for (let y = 0; y < h; y += size) {
      for (let x = (y / size) % 2 === 0 ? 0 : size; x < w; x += size * 2) {
        ctx.fillStyle = "rgba(255,255,255,1)";
        ctx.fillRect(x, y, size, size);
      }
    }
    ctx.restore();
  }, []);

  // Draw one tile (cover fit centered in its tile column)
  const drawTile = React.useCallback(
    (
      ctx: CanvasRenderingContext2D,
      img: ImageBitmap | HTMLImageElement,
      index: number,
      total: number,
    ) => {
      const { canvas } = ctx;
      const W = canvas.width,
        H = canvas.height;
      const tileW = Math.ceil(W / Math.max(1, total));

      // Compute "cover" fit into tile rect (tileW x H)
      const iw = (img as any).width as number;
      const ih = (img as any).height as number;

      // Scale to cover: choose the larger scale
      const sx = tileW / iw;
      const sy = H / ih;
      const scale = Math.max(sx, sy);

      const dw = Math.round(iw * scale);
      const dh = Math.round(ih * scale);

      const dx = index * tileW + Math.floor((tileW - dw) / 2);
      const dy = Math.floor((H - dh) / 2);

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img as any, dx, dy, dw, dh);
    },
    [],
  );

  // Progressive paint effect: paints as each image arrives; cancels on change/unmount.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let aborted = false;
    const controllers: AbortController[] = [];

    // ✅ prevent overlapping paintAll runs (RO + DPR + initial)
    let painting = false;
    let repaintQueued = false;

    const cancelAll = () => {
      controllers.forEach((c) => c.abort());
      controllers.length = 0;
    };

    const paintAll = async () => {
      if (aborted) return;

      // If already painting, queue exactly one more repaint.
      if (painting) {
        repaintQueued = true;
        return;
      }

      painting = true;
      repaintQueued = false;

      try {
        // Cancel any in-flight loads from previous run
        cancelAll();

        // Ensure proper backing store size
        resizeCanvas(canvas, height);

        // Base layer
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        paintBase(ctx);

        const n = urls.length;
        if (!n) return;

        for (let i = 0; i < n; i++) {
          if (aborted) return;
          const u = urls[i];
          if (!u) continue;

          const ctrl = new AbortController();
          controllers.push(ctrl);

          try {
            const img = await loadImageAny(u, ctrl.signal);
            if (aborted) {
              closeBitmap(img);
              return;
            }
            drawTile(ctx, img, i, n);
            closeBitmap(img);
          } catch {
            if (aborted) return;
            // leave placeholder
          }
        }
      } finally {
        painting = false;

        // If something requested a repaint while we were painting, do one more pass.
        if (!aborted && repaintQueued) {
          repaintQueued = false;
          // next frame to avoid sync recursion
          requestAnimationFrame(() => {
            if (!aborted) paintAll();
          });
        }
      }
    };

    // ✅ Resize observer (debounced via RAF)
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            if (aborted) return;
            requestAnimationFrame(() => {
              if (aborted) return;
              paintAll();
            });
          })
        : null;

    ro?.observe(canvas);

    // ✅ DPR change listener (store handler reference so cleanup works)
    let dprMql: MediaQueryList | null = null;
    let onDprChange: (() => void) | null = null;

    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      const dpr = window.devicePixelRatio || 1;
      dprMql = window.matchMedia(`(resolution: ${dpr}dppx)`);
      onDprChange = () => {
        if (aborted) return;
        paintAll();
      };

      dprMql.addEventListener?.("change", onDprChange);
      dprMql.addListener?.(onDprChange);
    }

    // Initial paint
    paintAll();

    return () => {
      aborted = true;
      cancelAll();
      ro?.disconnect();

      if (dprMql && onDprChange) {
        dprMql.removeEventListener?.("change", onDprChange);
        dprMql.removeListener?.(onDprChange);
      }
    };
  }, [urls, height, resizeCanvas, paintBase, drawTile]);

  return (
    <div className="relative h-full w-full">
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        aria-label="Video thumbnails"
        role="img"
      />
      {loading && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-[11px] text-white/55">
          generating…
        </div>
      )}
    </div>
  );
}

/* ───────────────── helpers ───────────────── */

function closeBitmap(img: ImageBitmap | HTMLImageElement) {
  (img as ImageBitmap).close?.();
}

async function loadImageAny(
  url: string,
  signal?: AbortSignal,
): Promise<ImageBitmap | HTMLImageElement> {
  // Prefer ImageBitmap (fast decode), fall back to HTMLImageElement
  if (typeof createImageBitmap === "function") {
    const blob = await fetch(url, { signal }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
    return await createImageBitmap(blob);
  }

  return await loadHTMLImage(url, signal);
}

function loadHTMLImage(url: string, signal?: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager"; // we want these quickly
    img.crossOrigin = "anonymous";
    img.src = url;

    const abort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    const onLoad = () => {
      cleanup();
      resolve(img);
    };
    const onError = () => {
      cleanup();
      reject(new Error("Image load error"));
    };

    const cleanup = () => {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
      signal?.removeEventListener("abort", abort);
    };

    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);
    signal?.addEventListener("abort", abort);
  });
}
